import React, { useEffect, useState } from "react";
import { isStaffEmail } from "@utils/user_utils";
import {
	DatePicker,
  Button,
  Space,
  Checkbox,
  Select,
  Input,
  Modal,
  Popconfirm
} from "antd";
import { audioMessage as message } from '../Common/Notifications/audioMessage';
import { Navbar } from "../Navigation/Navbar";
import { LoadingComponent } from "../Common/LoadingComponent";
import { HelpButton } from "../Common/HelpButton";
import { useSelector, useDispatch } from "react-redux";
import { DATE_FORMAT } from "../../Utils/date_utils";
import { mapDataForScatterplot } from '../../mappers/model/Scatterplot';
import {ScatterplotChart} from '../Charts/ScatterplotChart';
import BlurredScatterplot from "./blurred-scatterplot.png";
import {
  getScatterplotData,
  setScatterplotData,
  setScatterplotFilters,
  setScatterplotError,
  getTeamPresets,
  setTeamPresets
} from "../../store/actions/team.actions";
import dayjs from "dayjs";
import "../../Styles/styles-Scatterplot.css";
import { OptionGroupSelect } from '../Compare/Components_1v1/OptionGroupSelect'
import { useDataAccess } from "@components/PermissionedComponents";
import { trainingAPI } from "../../api/TrainingAPI";
import { PremiumFeatureDisabled } from "../Common/PremiumFeatureDisabled";
import { CloseOutlined } from '@ant-design/icons';
import { AdminNotice } from "../Settings/AdminNotice";
import { useAuth0 } from "@auth0/auth0-react";
import { isWellnessScoreMetric } from "@utils/wellness_utils";

const { RangePicker } = DatePicker;
const { Option } = Select;

export const Scatterplot = ()  => {
  const dispatch = useDispatch();
  const PRESET_TYPE = "scatterplot";

  const {
    positions,
    playerGroups: groups,
    allPlayers,
		all_metric_types,
    info: { team_colors: teamColors, scatterplot_enabled },
    scatterplot_filters: {
      startDate,
      endDate,
      xAxisMetric,
      yAxisMetric,
      playerSelections,
      groupByGameDate,
      useAvgValues
    },
    scatterplot_data,
    scatterplot_error,
    presets
	} = useSelector(({ team }) => team);

  const { canAccess, ready: permissionsReady } = useDataAccess();

  const { user } = useAuth0();
  const isStaff = isStaffEmail(user?.email);

  const [loading, setLoading] = useState(false);
  const [playerPosGroupOptions, setPlayerPosGroupOptions] = useState([]);
  const [metricOptions, setMetricOptions] = useState([]);
  const [xAxisLabel, setXAxisLabel] = useState('');
  const [yAxisLabel, setYAxisLabel] = useState('');
  const [selectedPreset, setSelectedPreset] = useState(null);
  const [xPlotlines, setXPlotlines] = useState([]);
  const [yPlotlines, setYPlotlines] = useState([]);
  const [open, setOpen] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [newPresetName, setNewPresetName] = useState('');
  const [modalText, setModalText] = useState('Once saved you can quick access them from the preset dropdown.');
  const [hasSubmitted, setHasSubmitted] = useState(false);

  const resetAllValues = () => {
    dispatch(setScatterplotFilters({
      startDate: dayjs().subtract(2, "weeks").format(DATE_FORMAT),
      endDate: dayjs().format(DATE_FORMAT),
      xAxisMetric: null,
      yAxisMetric: null,
      playerSelections: [],
      groupByGameDate: false,
      useAvgValues: false
    }));
    dispatch(setScatterplotError(null));
    dispatch(setScatterplotData({ data: []}));
    setXAxisLabel('');
    setYAxisLabel('');
    setNewPresetName('');
    setSelectedPreset(null);
    setLoading(false);
    setXPlotlines([]);
    setYPlotlines([]);
    setHasSubmitted(false);
  }

  useEffect(() => {
    resetAllValues();
    dispatch(getTeamPresets({type: [PRESET_TYPE]}))
  }, [])

  useEffect(() => {
    if (scatterplot_error) {
      setLoading(false);
      message.error(scatterplot_error);
      dispatch(setScatterplotError(null));
    }
  }, [scatterplot_error])

  useEffect(() => {
    setLoading(false);

    if (hasSubmitted && scatterplot_data && scatterplot_data.data && scatterplot_data.data.length === 0) {
      message.error("No data available for the selected criteria. Try adjusting your filters or date range.");
      setHasSubmitted(false);
    }
  }, [scatterplot_data])

  const getOptionGroup = (optionGroupName, JSONOptions, metric_type) => {
    let metricObj = {}, metricOps = [];
    metricObj["label"] = <span>{optionGroupName}</span>;
    metricObj["title"] = optionGroupName;
    
    for (let i = 0; i < JSONOptions.length; i++) {
      const metric = JSONOptions[i];
      if (["gps", "forceplate", "testing", "wellness", "nutrition", "cognition"].includes(metric_type)) {
        const isWellnessNonNumeric = metric_type === "wellness" && 
          metric["answer_type"] && 
          ["string", "bodymap"].includes(metric["answer_type"].toLowerCase());
        
        metricOps.push({
          label: <span>{metric["name"]}</span>,
          // UDM entries carry a `type-c<id>` unique_identifier; native metrics key off metric_id.
          value: metric["is_user_defined"]
            ? metric["unique_identifier"]
            : `${metric_type}-${metric["metric_id"]}`,
          key: metric["is_user_defined"] ? `c${metric["metric_id"]}` : metric["metric_id"],
          disabled: isWellnessNonNumeric
        });
      }
      else if (metric_type === "player") {
        metricOps.push({
          label: <span>{`${metric["first_name"]} ${metric["last_name"]}`}</span>,
          value: `${metric_type}-${metric["player_id"]}`,
          key: metric["player_id"]
        });
      }
      else if (metric_type === "pos") {
        metricOps.push({
          label: <span>{metric}</span>,
          value: `${metric_type}-${metric}`,
          key: metric
        });
      }
      else if (metric_type === "group") {
        metricOps.push({
          label: <span>{metric["name"]}</span>,
          value: `${metric_type}-${metric["player_group_id"]}`,
          key: metric["player_group_id"]
       });
      }
    }

    if (metric_type === "gps") {
      metricObj["options"] = metricOps;
    } else {
      metricObj["options"] = metricOps.sort((a, b) => {
        const valueA = a?.label?.props?.children || "";
        const valueB = b?.label?.props?.children || "";
        return valueA.localeCompare(valueB);
      });
    }
    return metricObj;
  }

  useEffect(() => {
    let groupOptions = [];
    for (const [metric_type, metrics] of Object.entries(all_metric_types)) {

      const metricsFiltered = metrics.filter((e) => (e.visible === true || e.visible === undefined) && !isWellnessScoreMetric(e))
      if (metricsFiltered.length > 0) {
        let groupLabel;
        if (metric_type === "gps") groupLabel = "GPS";
        else groupLabel = metric_type.charAt(0).toUpperCase() + metric_type.slice(1).toLowerCase(); //Capitalize

        groupOptions.push({
          ...getOptionGroup(groupLabel, metricsFiltered, metric_type),
          // metric_type doubles as the data-stream name (gps, wellness, ...).
          disabled: !canAccess(metric_type),
        });
      }
    }

    // add "Other" group with "Date" option
    groupOptions.push({
      label: <span>Other</span>,
      title: "Other",
      options: [{
        label: <span>Date</span>,
        value: "date",
        key: "date"
      }]
    });

    setMetricOptions(groupOptions)
  }, [all_metric_types, permissionsReady])

  useEffect(() => {
    let groupOptions = [], metricObj = {};
    metricObj["label"] = <span>Team</span>;
    metricObj["title"] = "Team";
    metricObj["options"] = [{
      label: <span>Full Team</span>,
      value: "full-team",
      key: "full-team"
    }];
    
    groupOptions.push(metricObj);

    if (allPlayers.length > 0) {
      groupOptions.push(getOptionGroup("Players", allPlayers, "player"));
    }

    if (positions.length > 0) {
      groupOptions.push(getOptionGroup("Positions", positions, "pos"));
    }

    if (groups.length > 0) {
      groupOptions.push(getOptionGroup("Groups", groups, "group"));
    }

    setPlayerPosGroupOptions(groupOptions);
    dispatch(setScatterplotFilters({playerSelections: ['full-team']}));
  }, [positions, groups, allPlayers])

  const handleDateRangeChange = (datesMoments, dateStrings) => {
    dispatch(setScatterplotFilters({startDate: datesMoments?.[0], endDate: datesMoments?.[1]}));
  }

  const handleOptionGroupChange = (value) => {
    dispatch(setScatterplotFilters({playerSelections: value}));
  }

  const handleMetricOptionGroupChange = (value, axis, options) => {

    const label = options?.label?.props?.children || '';

    if (axis === "X") {
      dispatch(setScatterplotFilters({xAxisMetric: value}));
      setXAxisLabel(label)
      if (value === 'date') {
        setXPlotlines([]);
      }
    } else if (axis === "Y") {
      dispatch(setScatterplotFilters({yAxisMetric: value}));
      setYAxisLabel(label)
      if (value === 'date') {
        setYPlotlines([]);
      }
    } else {
      message.error("Unknown scatterplot axis")
    }
    
  }

  const convertStringToMoment = (dateString) => {
    if (!dateString) {
      return null;
    }
    return dayjs(dateString);
  }


  const handleSubmitClick = () => {

    if (!startDate || !endDate) {
      message.error("Please select valid start and end dates ");
      return
    }

    if (!xAxisMetric || !yAxisMetric) {
      message.error("Please select valid metrics value for both X and Y axis ");
      return
    }

    if (xAxisMetric === yAxisMetric) {
      message.error("X axis and Y axis cannot have the same metric");
      return
    }

    if (!(playerSelections && playerSelections.length > 0)) {
      message.error("Please select valid values from drop-down menu for team/player/position/group");
      return
    }

    setLoading(true);
    setHasSubmitted(true);
    dispatch(getScatterplotData());
  }

  const onCheckboxChange = (e, key) => {
    dispatch(setScatterplotFilters({[key]: e.target?.checked || false}));
  };

  const handleOk = async () => {
    setNewPresetName('')

    if(!newPresetName) {
      const enterNameError = "Please enter a valid preset name"
      console.error(enterNameError)
      message.error(enterNameError)
      return;
    }

    setConfirmLoading(true);

    const presetData = {
      name: newPresetName,
      type: 'scatterplot',
      config: {
        xAxisMetric,
        yAxisMetric,
        xAxisLabel,
        yAxisLabel,
        xPlotlines,
        yPlotlines,
        playerSelections,
        groupByGameDate,
        useAvgValues
      }
    }

    try {
			const updatedPresets = await trainingAPI.createPreset(presetData);
      dispatch(setTeamPresets(updatedPresets))
      setSelectedPreset(newPresetName)
      message.info("Preset saved")
		} catch (err) {
      const errorMessage = err?.response?.data || "Error saving preset"
			console.error(err)
      message.error(errorMessage)
      setConfirmLoading(false);
      return
		}

    setOpen(false);
    setConfirmLoading(false);
  };

  const handleCancel = () => {
    console.log('Clicked cancel button');
    setOpen(false);
  };

  const showModal = () => {
    setOpen(true);
  };

  const deleteItem = async (item) => {
    const presetOfInterest = presets?.find((preset) => preset.preset_id === item.preset_id)
    await trainingAPI.deletePreset(presetOfInterest.preset_id)
    const updatedPreset = presets.filter(preset => preset.preset_id !== presetOfInterest.preset_id)
    dispatch(setTeamPresets(updatedPreset))
    setNewPresetName('')

    if (updatedPreset.length === 0 ) {
      setSelectedPreset(null)
      resetAllValues()
    } else {
      handlePresetChange(updatedPreset[0].name)
    }
  }

  const handlePresetChange = (presetName) => {
    setSelectedPreset(presetName)
    const preset = presets?.find( (p) => p?.name === presetName)
    if (preset?.config) {
      const {
        xAxisMetric,
        yAxisMetric,
        xAxisLabel,
        yAxisLabel,
        xPlotlines,
        yPlotlines,
        playerSelections,
        groupByGameDate,
        useAvgValues
      } = preset?.config;

      dispatch(setScatterplotFilters({
        xAxisMetric,
        yAxisMetric,
        playerSelections,
        groupByGameDate,
        useAvgValues
      }));
      setXAxisLabel(xAxisLabel);
      setYAxisLabel(yAxisLabel);
      setXPlotlines(xPlotlines);
      setYPlotlines(yPlotlines);
    }
  }


  if (!scatterplot_enabled && !isStaff) {
    return (
      <PremiumFeatureDisabled
          bg_image={BlurredScatterplot}
          feature_name={"Scatterplot"}
          info_text={<>
          This is an elite feature that will allow you to build correlations between any data point within your system. <br/> <br/> 
          If you are interested in adding this to your platform, please contact your sales rep!</>}
        />
      )
  }

  return (
    <>
    <Navbar />
      <HelpButton page="Scatterplot" />
      {(!scatterplot_enabled && isStaff) && (
        <div style={{ margin: '16px', maxWidth: '800px', marginLeft: 'auto', marginRight: 'auto' }}>
          <AdminNotice />
        </div>
      )}
      <LoadingComponent loading={loading}>
        <div className="scatterplot-container">
          <div className="scatterplot-filter-container">
            <div className="filter-column" style={{flex: '1 1 200px', minWidth: '200px', marginLeft: '1rem', marginRight: '0.5rem'}}>
              <div className="width-100">
                <RangePicker
                  onChange={handleDateRangeChange}
                  value={(!startDate && !endDate) ? null : [convertStringToMoment(startDate), convertStringToMoment(endDate)]}
                  style={{ width: '100%' }}
                />
              </div>
              <div className="width-100">
                <Select
                  allowClear
                  showSearch
                  placeholder="Select a preset"
                  value={selectedPreset}
                  style={{ width: '100%' }}
                  onChange={handlePresetChange}
                >
                  {presets?.length ? presets.map((preset, i) => (
                    <Option key={i} value={preset.name}>
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                        }}
                      >
                        <span>{preset.name}</span>
                          <Popconfirm
                            title="Are you sure to delete?"
                            onConfirm={() => {deleteItem(preset)}}
                            okText="Yes"
                            cancelText="No"
                          >
                            <Button
                              type="link"
                              icon={<CloseOutlined />}
                              style={{ padding: 0 }}
                            />
                          </Popconfirm>
                      </div>
                    </Option>
                  )) : null}
                </Select>
              </div>
            </div>
            <div className="filter-column" style={{flex: '2 1 300px', minWidth: '300px'}}>
              <div className="plotline-container width-100">
                <div className="width-75">
                  <span style={{ marginRight: '0.5rem', width: '10%' }}>X Axis</span>
                    <OptionGroupSelect 
                      selectedOptionVal={xAxisMetric}
                      options={metricOptions}
                      placeholder="Choose Metric For X Axis"
                      width={'85%'}
                      mode={null}
                      handleOptionGroupChange={(val, label) => handleMetricOptionGroupChange(val, "X", label)}
                    />
                </div>
                <div className="width-25">
                  <Select 
                    className="player-group-pos-left"
                    style={{
                      marginLeft: '0.5rem',
                      width: '100%'
                    }}
                    mode="tags"
                    value={xPlotlines}
                    maxTagCount='responsive'
                    placeholder="Enter an x axis plotline"
                    onChange={setXPlotlines}
                    disabled={xAxisMetric === 'date'}
                  />
                </div>
              </div>
              <div className="plotline-container width-100">
                <div className="width-75">
                  <span style={{ marginRight: '0.5rem', width: '10%' }}>Y Axis</span>
                  <OptionGroupSelect 
                    selectedOptionVal={yAxisMetric}
                    options={metricOptions?.filter((option) => option.title !== 'Other') ?? []}
                    placeholder="Choose Metric For Y Axis"
                    width={'85%'}
                    mode={null}
                    handleOptionGroupChange={(val, label) => handleMetricOptionGroupChange(val, "Y", label)}
                  />
                </div>
                <div className="width-25">
                  <Select 
                    className="player-group-pos-left"
                    style={{
                      marginLeft: '0.5rem',
                      width: '100%'
                    }}
                    mode="tags"
                    value={yPlotlines}
                    maxTagCount='responsive'
                    placeholder="Enter a y axis plotline"
                    onChange={setYPlotlines}
                    disabled={yAxisMetric === 'date'}
                  />
                </div>
              </div>
            </div>

            {/* 
              player selection
            */}
            <div className="filter-column" style={{flex: '1 1 200px', minWidth: '200px'}}>
              <div className="width-100">
                <OptionGroupSelect
                  className="player-group-pos-left" 
                  selectedOptionVal={playerSelections}
                  options={playerPosGroupOptions}
                  placeholder="Full Team"
                  width={'90%'}
                  sideId='left' //just used to form unique key
                  handleOptionGroupChange={handleOptionGroupChange}
                />
              </div>

              <div className="width-100">
                <Checkbox
                  className=""
                  checked={groupByGameDate}
                  onChange={(e) => onCheckboxChange(e, 'groupByGameDate')}
                >
                  Group by game date
                </Checkbox>
                <Checkbox
                  className=""
                  checked={useAvgValues}
                  onChange={(e) => onCheckboxChange(e, 'useAvgValues')}
                >
                  Team averages
                </Checkbox>
              </div>
            </div>
            {/* 
              submit button
            */}
            <div className="filter-column" style={{flex: '0 0 130px', minWidth: '130px', marginRight: '1rem'}}>
              <div className="scatterplot-button" style={{marginBottom: '0.1rem'}}>
                <Button
                  className="scatterplot-button"
                  type="primary"
                  onClick={showModal}
                  loading={loading}
                >
                  Save as preset
                </Button>
              </div>
              <div className="scatterplot-button">
                <Button
                  className="scatterplot-button"
                  type="primary"
                  onClick={handleSubmitClick}
                  loading={loading}
                >
                  Submit
                </Button>
              </div>
            </div>
          </div>

            {scatterplot_data?.data?.length ? (
              <div className="chart-container">
                <div className="inner-chart">
                  <ScatterplotChart
                        height={'inherit'}
                        options={mapDataForScatterplot({
                          data: scatterplot_data?.data,
                          xAxisMetricName: xAxisLabel,
                          yAxisMetricName: yAxisLabel,
                          xAxisMetric,
                          yAxisMetric,
                          xPlotlines, // TODO: get from presets in store
                          yPlotlines, // TODO: get from presets in store
                          colors: teamColors.all
                        })}
                        data={scatterplot_data?.data}
                        xPlotlines={xPlotlines}
                        yPlotlines={yPlotlines}
                  />
                </div>
              </div>
            ) : <></>}

        </div>

        <Modal
          title="Save options"
          open={open}
          onOk={handleOk}
          confirmLoading={confirmLoading}
          onCancel={handleCancel}
        >
          <p>{modalText}</p>
          <Input
            placeholder="Enter a name for your settings"
            value={newPresetName}
            onChange={(e) => setNewPresetName(e?.target?.value)}
          />
        </Modal>
      </LoadingComponent>
    </>
  );
}

export default Scatterplot;